/**
 * Copiar al portapapeles desde dentro de un modal.
 *
 * El botón de copiar de swalErrorBox (hoy «Copiar detalle») contestaba que no
 * había copiado nada por dos motivos que se tapaban entre sí:
 *
 *  1. `navigator.clipboard` solo existe en contexto seguro (https o localhost).
 *     En el probe por http plano no existe, y aun existiendo, writeText() se
 *     rechaza con NotAllowedError si el documento no tiene el foco. El código
 *     viejo solo usaba el respaldo cuando la API FALTABA: si estaba y se
 *     rechazaba, reportaba fracaso sin intentar nada más.
 *
 *  2. El respaldo colgaba el <textarea> de document.body. Cuando el error sale
 *     encima de un modal de Bootstrap (el de cobranza) o de un popup de
 *     SweetAlert2, sus focus traps devuelven el foco a su propio elemento en
 *     cuanto aterriza fuera; el textarea pierde el foco, la selección se
 *     colapsa y execCommand('copy') devuelve false.
 *
 * Aquí el respaldo cuelga el textarea DENTRO del contenedor que ya tiene el
 * foco (el popup o el modal), así ningún trap se lo quita, y siempre se intenta
 * después de que la API asíncrona falla, no en su lugar.
 *
 * Todo lo que toca el DOM entra por parámetro (`doc`, `nav`, `contenedor`) para
 * poder probarlo en el entorno `node` de vitest, sin jsdom.
 */

/**
 * Arma el texto que se copia. Código y mensaje siempre viajan juntos: sin el
 * código soporte no puede buscar, y sin el mensaje nadie entiende el código.
 *
 * @param {Object} partes
 * @param {string} [partes.titulo]
 * @param {string} [partes.codigo]   clave del PAC/SAT (CFDI33xxx, etc.)
 * @param {string} [partes.mensaje]
 * @param {string} [partes.detalle]  detalle técnico crudo
 * @returns {string}
 */
export function textoDeError(partes) {
  var p = partes || {};
  var lineas = [];
  var titulo = limpiar(p.titulo);
  var codigo = limpiar(p.codigo);
  var mensaje = limpiar(p.mensaje);
  var detalle = limpiar(p.detalle);

  // El título por omisión de swalErrorBox («Revisemos esto»; antes «Error») no aporta nada al copiar.
  var porOmision = ['error', 'revisemos esto'];
  if (titulo !== '' && porOmision.indexOf(titulo.toLowerCase()) === -1) lineas.push(titulo);
  if (codigo !== '') lineas.push('Código: ' + codigo);
  if (mensaje !== '') lineas.push(mensaje);
  if (detalle !== '' && detalle !== mensaje) lineas.push('Detalle técnico: ' + detalle);

  return lineas.join('\n');
}

function limpiar(v) {
  return String(v == null ? '' : v).replace(/\s+$/, '').replace(/^\s+/, '');
}

/**
 * Copia `texto` al portapapeles. Nunca lanza: resuelve a true/false.
 *
 * @param {string} texto
 * @param {Object} [opciones]
 * @param {Element} [opciones.contenedor] elemento con el foco (popup de Swal o
 *        modal). El textarea temporal se cuelga de ahí para que ningún focus
 *        trap le quite el foco antes de copiar.
 * @param {Document} [opciones.doc]
 * @param {Navigator} [opciones.nav]
 * @returns {Promise<boolean>}
 */
export function copiarTexto(texto, opciones) {
  var o = opciones || {};
  var doc = o.doc || (typeof document !== 'undefined' ? document : null);
  var nav = o.nav || (typeof navigator !== 'undefined' ? navigator : null);
  var valor = String(texto == null ? '' : texto);

  if (valor === '') return Promise.resolve(false);

  var respaldo = function () {
    return Promise.resolve(copiarConTextarea(valor, o.contenedor, doc));
  };

  if (nav && nav.clipboard && typeof nav.clipboard.writeText === 'function') {
    try {
      return Promise.resolve(nav.clipboard.writeText(valor)).then(
        function () { return true; },
        // Rechazo típico: NotAllowedError porque el documento perdió el foco.
        // No es el final del camino, es la señal de usar el respaldo.
        respaldo
      );
    } catch (e) {
      return respaldo();
    }
  }

  return respaldo();
}

/**
 * Respaldo universal: <textarea> temporal + execCommand('copy').
 * Funciona en http plano, que es donde `navigator.clipboard` ni existe.
 *
 * @returns {boolean}
 */
export function copiarConTextarea(texto, contenedor, doc) {
  var d = doc || (typeof document !== 'undefined' ? document : null);
  if (!d || typeof d.createElement !== 'function' || typeof d.execCommand !== 'function') return false;

  // Colgarlo del contenedor enfocado (popup/modal) y no de <body>: los focus
  // traps de Bootstrap y SweetAlert2 devuelven el foco a su elemento en cuanto
  // sale, y la selección del textarea se pierde antes de copiar.
  var host = (contenedor && typeof contenedor.appendChild === 'function') ? contenedor : d.body;
  if (!host) return false;

  var previo = d.activeElement;
  var ta = d.createElement('textarea');
  ta.value = texto;
  // readonly evita que iOS abra el teclado; sigue siendo seleccionable.
  if (typeof ta.setAttribute === 'function') ta.setAttribute('readonly', '');
  if (ta.style) {
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.width = '1px';
    ta.style.height = '1px';
    ta.style.padding = '0';
    ta.style.border = 'none';
    ta.style.outline = 'none';
    ta.style.boxShadow = 'none';
    ta.style.background = 'transparent';
    // opacity 0, no display:none ni visibility:hidden: eso lo haría inseleccionable.
    ta.style.opacity = '0';
  }

  var ok = false;
  try {
    host.appendChild(ta);
    if (typeof ta.focus === 'function') ta.focus();
    if (typeof ta.select === 'function') ta.select();
    // iOS ignora select() en algunos casos; el rango explícito sí lo respeta.
    if (typeof ta.setSelectionRange === 'function') ta.setSelectionRange(0, texto.length);
    ok = d.execCommand('copy') === true;
  } catch (e) {
    ok = false;
  } finally {
    try { if (ta.parentNode) ta.parentNode.removeChild(ta); } catch (e2) { /* nada que hacer */ }
    // Devolver el foco a quien lo tenía: si no, el modal se queda sin foco y el
    // Esc deja de cerrarlo.
    try { if (previo && typeof previo.focus === 'function') previo.focus(); } catch (e3) { /* nada que hacer */ }
  }

  return ok;
}

export default {
  copiarTexto: copiarTexto,
  copiarConTextarea: copiarConTextarea,
  textoDeError: textoDeError,
};
